// Pure impact-recommendation logic — the port of v3's
// `components/Views/ImpactAnalysis/recommendationPrefill.ts`. No Vue, no HTTP, no store.
import type { CausalProfile } from '../models/impact.types'
import type {
  ImpactRecommendationCard,
  ImpactRecommendationExecution,
  ImpactRecommendationFrame,
  ImpactRecommendationPrefill,
  ImpactRecommendationQuery,
  ImpactRecommendationSection,
} from '../models/recommendation.types'

import { stripHtml } from '@/shared/lib/string/stripHtml'

/** Nodes whose text is markup plumbing, not prose (v3's exact list). */
const NON_CONTENT_SELECTOR = 'script,style,template,iframe,object,embed'

/**
 * Recommendation prose is LLM-authored HTML and the card renders its TEXT — there is no HTML render
 * surface for it anywhere, which is why this returns a string and not sanitized markup.
 *
 * Two steps, and the order matters. First the non-content nodes are removed WITH their contents
 * (v3 `sanitizeRecommendationRationale`), then `stripHtml` — the shared owner — takes the text and
 * collapses whitespace.
 *
 * `shared/ui/sanitize`'s `sanitizeHtml` is deliberately NOT in that chain. It is an HTML→HTML
 * boundary for renderers, and DOMPurify's `FORBID_TAGS` UNWRAPS a forbidden element rather than
 * deleting it: `<style>.a{color:red}</style>` comes back as the bare text `.a{color:red}`, which
 * would then land in the card as prose. Its attribute/URL hardening buys nothing here either,
 * since the output is a text node. Proven by this module's spec.
 */
export function sanitizeRationale(html: unknown): string {
  if (typeof html !== 'string' || !html.trim()) return ''
  const parsed = new DOMParser().parseFromString(html, 'text/html')
  for (const node of parsed.body.querySelectorAll(NON_CONTENT_SELECTOR)) node.remove()
  return stripHtml(parsed.body.innerHTML)
}

/** Rank ascending; an unranked frame sinks while keeping its relative order. */
function sortByRank(frames: readonly ImpactRecommendationFrame[]): ImpactRecommendationFrame[] {
  return [...frames].sort((left, right) => {
    const leftRank = left.rank ?? Number.MAX_SAFE_INTEGER
    const rightRank = right.rank ?? Number.MAX_SAFE_INTEGER
    return leftRank - rightRank
  })
}

/**
 * Normalised platform name for the first badge. v3 folded separators and case before matching, so
 * `facebook_ads`, `Meta Ads` and `META` all reach the same label; anything unrecognised is passed
 * through verbatim, and only a missing value falls back.
 */
export function platformLabel(value: string | null): string | null {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase()
    .replaceAll(/[\s_-]+/gu, '')
  if (normalized.includes('google')) return 'Google Ads'
  if (normalized.includes('meta') || normalized.includes('facebook')) return 'Meta Ads'
  return value ? String(value) : null
}

/**
 * Seed values for the wizard, or `null` when the frame cannot describe a run.
 *
 * Requires a run payload with a profile slug AND both range ends — v3's exact guard. `frequency`
 * is forced to `one_time`: a recommendation is a one-off measurement of a change that already
 * happened, never a schedule, whatever the payload says.
 */
export function frameToPrefill(
  frame: ImpactRecommendationFrame | null | undefined,
): ImpactRecommendationPrefill | null {
  const payload = frame?.run_payload
  const dateStart = payload?.input_parameters.date_start
  const dateEnd = payload?.input_parameters.date_end
  const profileSlug = payload?.profile_slugs[0]
  if (!frame || !payload || !profileSlug || !dateStart || !dateEnd) return null

  return {
    recommendationId: frame.recommendation_id,
    title: frame.title || payload.name || '',
    rationaleText: sanitizeRationale(frame.rationale_html),
    eligibleSegmentCount: frame.eligible_segment_count,
    estimatedCredits: frame.estimated_credits,
    measurement: frame.measurement,
    runPayload: {
      ...payload,
      profile_slugs: [...payload.profile_slugs],
      input_parameters: { ...payload.input_parameters },
      frequency: 'one_time',
    },
  }
}

/** v3's identity fallback chain — the dismissal key, so dismissing one scan never hides the next. */
function sectionIdentity(
  execution: ImpactRecommendationExecution,
  recommendationIds: readonly string[],
): string {
  return (
    execution.execution_id ||
    execution.generated_at ||
    recommendationIds.join('|') ||
    `${execution.date_from ?? ''}:${execution.date_to ?? ''}:${
      execution.not_enough_data ? 'empty' : 'unknown'
    }`
  )
}

/**
 * Map one execution to the rendered section. Unlike module 23's equivalent this drops every frame
 * that cannot build a prefill — v3 filtered them out, and here that is load-bearing rather than
 * cosmetic: an impact card's only action is "Run this analysis", so a frame with no run payload
 * would render a button that cannot do anything.
 */
export function mapExecutionToSection(
  execution: ImpactRecommendationExecution | null | undefined,
): ImpactRecommendationSection | null {
  if (!execution) return null

  const recommendations: ImpactRecommendationCard[] = []
  for (const frame of sortByRank(execution.recommendations)) {
    const prefill = frameToPrefill(frame)
    if (!prefill) continue
    recommendations.push({
      recommendationId: prefill.recommendationId,
      title: frame.title || prefill.title,
      rationaleText: prefill.rationaleText,
      platformLabel: platformLabel(frame.platform) ?? '',
      changeTypeLabel: frame.change_type_label,
      profileName: frame.profile_name || frame.run_payload?.profile_slugs[0] || '',
      eligibleSegmentCount: frame.eligible_segment_count,
      affectedSpend: frame.affected_spend,
      estimatedCredits: frame.estimated_credits,
      prefill,
    })
  }

  return {
    identity: sectionIdentity(
      execution,
      recommendations.map((card) => card.recommendationId),
    ),
    dateFrom: execution.date_from,
    dateTo: execution.date_to,
    recommendations,
  }
}

/** Locate one frame inside an execution by id (the `?recommendation=` deep-link half). */
export function findImpactRecommendation(
  execution: ImpactRecommendationExecution | null | undefined,
  recommendationId: string | null | undefined,
): ImpactRecommendationFrame | null {
  if (!execution?.recommendations.length || !recommendationId) return null
  return (
    execution.recommendations.find(
      (frame) => frame.recommendation_id === String(recommendationId),
    ) ?? null
  )
}

/**
 * The recommended profile, but only if it is still selectable. An inactive or non-workable profile
 * resolves to `null`, which the wizard surfaces as "pick another profile" rather than silently
 * running against something the user cannot see (v3 `findSelectableRecommendationProfile`).
 */
export function findSelectableProfile(
  profiles: readonly CausalProfile[],
  slug: string | null | undefined,
): CausalProfile | null {
  if (!slug) return null
  const profile = profiles.find((entry) => entry.slug === slug)
  if (!profile || profile.is_active === false || profile.config.frontend_workable === false) {
    return null
  }
  return profile
}

/**
 * `target_metric` → `Target Metric` (v3 `RecommendationPrefillInfo.formatLabel`).
 *
 * Deliberately NOT module 08's metric catalog: the measurement block is a read-only echo of what
 * the engine chose, the fields it names are not always report metrics, and v3 rendered the raw
 * field humanised. Routing it through the catalog would silently change the copy and add a
 * catalog dependency to a panel that has no other use for one.
 */
export function humanizeMetricLabel(value: string): string {
  return value.replaceAll('_', ' ').replace(/\b\p{L}/gu, (letter) => letter.toLocaleUpperCase())
}

/** First usable string of a route-query value (vue-router hands `string | string[] | null`). */
function singleQueryValue(value: unknown): string | null {
  if (Array.isArray(value)) return singleQueryValue(value[0])
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

/** Read `?reco_execution=<id>&recommendation=<id>`. Both halves are required to act. */
export function readImpactRecommendationQuery(
  query: Record<string, unknown>,
): ImpactRecommendationQuery {
  const executionId = singleQueryValue(query.reco_execution)
  const recommendationId = singleQueryValue(query.recommendation)
  return { executionId, recommendationId, shouldFetch: Boolean(executionId && recommendationId) }
}
